<template>
  <div class="container" @click="$emit('titleClicked')">
    <template v-if="!display2ndOrderReferences">
      <div class="document-card-wrapper">
        <div class="image-container">
          <div v-if="!loaded" class="placeholder">
            <slot name="placeholder">
              <div class="skeleton" />
            </slot>
          </div>
          <img v-show="loaded" :src="thumbUrl" alt="thumb" @load="onLoad" @error="onError" />
        </div>
        <div class="document-details">
          <h4 class="document-title" @click.stop="$emit('titleClicked')">
            {{ title }}
          </h4>
        </div>
      </div>
      <div class="snippet-container">
        <div>
          <span>{{ 'Excerpt from Page #' + page }}</span>
        </div>
        <div class="snippet">
          {{ snippet }}
        </div>
      </div>
    </template>
    <div v-else-if="overflowReferences.length" class="second-order-content second-order-overflow">
      <div v-for="(ref, index) in overflowReferences" :key="`${ref.citationId || ref.externalId || index}`" class="overflow-item">
        <span class="overflow-index">{{ ref.overflowLabel }}.</span>
        <SecondOrderReferenceItem
          class="overflow-item-body"
          :title="ref.text || ref.name"
          :source="{ ...source, unsubstantiated: ref.unsubstantiated }"
          :reference="ref"
          @title-clicked="$emit('titleClicked', ref)"
          @preview="(page) => $emit('preview', { reference: ref, page })"
        />
      </div>
    </div>
    <div v-else class="second-order-content">
      <SecondOrderReferenceItem
        :title="title"
        :source="source"
        :reference="reference"
        @title-clicked="$emit('titleClicked')"
        @preview="$emit('preview', $event)"
      />
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

import SecondOrderReferenceItem from '~/components/conversation/chatMessage/SecondOrderReferenceItem.vue';
import useConversations from '~/composables/useConversations';

export default {
  components: { SecondOrderReferenceItem },
  emits: ['titleClicked', 'preview'],
  props: {
    title: {
      type: String,
      default: '',
    },
    page: {
      type: String,
      default: '',
    },
    snippet: {
      type: String,
      default: '',
    },
    thumbUrl: {
      type: String,
      default: '',
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    reference: {
      type: Object,
      default: () => ({}),
    },
    overflowReferences: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { display2ndOrderReferences } = useConversations();

    const loaded = ref(false);

    const onLoad = () => (loaded.value = true);
    const onError = () => (loaded.value = false);

    return {
      loaded,
      onError,
      onLoad,
      display2ndOrderReferences,
    };
  },
};
</script>

<style scoped lang="scss">
.container {
  background-color: $bb-neutral-0;
  width: 400px;
  max-height: 232px;
  overflow: hidden;
  border: 1px solid #ddd;
  border-radius: $bb-spacing-2;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column; /* Stack rows */
  z-index: 1000;

  .snippet-container {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    padding-inline: $bb-spacing-3;
    max-height: 260px; /* Subtract padding */

    .snippet {
      @include bb-regular-text-styles('xs');
      overflow: hidden;
      color: $bb-neutral-1;
      display: -webkit-box;
      line-clamp: 5;
      -webkit-line-clamp: 5; /* Number of lines before ellipsis */
      -webkit-box-orient: vertical;
      box-sizing: border-box;
      padding-top: 0;
      padding-bottom: 0; /* Prevents adding extra height */
      margin-bottom: $bb-spacing-3;
    }

    span {
      @include bb-medium-text-styles('xs');
      color: $bb-neutral-1;
      text-decoration: underline;
      letter-spacing: 0.1px;
      font-size: 13px;
    }
  }

  .second-order-content.second-order-overflow {
    overflow-y: auto;
    max-height: 232px;
    gap: 0;

    .overflow-item {
      display: flex;
      gap: $bb-spacing-2;
      padding: $bb-spacing-2 0;

      &:not(:first-child) {
        border-top: 1px solid $bb-neutral-4;
      }

      .overflow-index {
        @include bb-medium-text-styles('xs');
        color: $bb-neutral-2;
        flex-shrink: 0;
      }

      .overflow-item-body {
        min-width: 0; // lets SecondOrderReferenceItem's own text-truncation styles take effect
      }
    }
  }

  .second-order-content {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    padding: $bb-spacing-3;
  }

  .document-card-wrapper {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-2;
    margin-bottom: $bb-spacing-3;
    background-color: $bb-neutral-6;
    padding: $bb-spacing-4;

    .image-container {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      width: 64px;
      min-width: 64px;
      height: 64px;
      gap: $bb-spacing-1;
      position: relative;

      .placeholder {
        position: absolute;
        inset: 0;
      }

      .skeleton {
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
        animation: shimmer 1.4s infinite;
      }

      @keyframes shimmer {
        0% {
          background-position: -200px 0;
        }
        100% {
          background-position: 200px 0;
        }
      }

      .default-thumb {
        align-self: flex-start;
        width: 60px;
        height: 48px;
        background-color: $bb-neutral-4;
      }

      img {
        max-width: 60px;
        max-height: 48px;
        align-self: center;
      }
    }

    .document-details {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-2;
      max-width: 100%;
      overflow: hidden;

      .document-title {
        @include bb-medium-text-styles('md');
        line-height: $bb-spacing-5;
        color: $bb-neutral-1;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
  }
}
</style>
